<template>
  <div>
    <CVButton variant="secondary" class="mt-4" @click="showModal = true">
      {{ t('addCleaningFee') }}
    </CVButton>

    <Modal v-model="showModal" :title="t('addCleaningFee')">
      <div v-if="isLoading" class="flex justify-center py-8">
        <Spinner />
      </div>

      <div v-else-if="fees.length === 0" class="py-8 text-center text-obsidian-500">
        {{ t('noCleaningFeesAvailable') }}
      </div>

      <div v-else class="space-y-4">
        <h2 class="font-bold lg:text-lg">{{ t('addCleaningFee') }}</h2>
        <div
          v-for="fee in fees"
          :key="fee.id"
          class="rounded-lg border p-4"
          :class="
            selectedFeeId === fee.id ? 'outline outline-2 outline-offset-2 outline-hazel-500' : ''
          "
        >
          <label class="flex cursor-pointer items-start gap-3">
            <input
              v-model="selectedFeeId"
              type="radio"
              :value="fee.id"
              :class="radiobutton({ class: 'mt-1 h-4 w-4' })"
            />
            <div class="flex-1">
              <h4 class="font-medium">{{ getLocalizedField(fee.name, locale) }}</h4>
              <p class="mt-1 text-sm text-obsidian-600">
                {{ formatCurrency(fee.price) }}
                <span
                  v-if="fee.photoRequired"
                  class="inline-block rounded bg-yellow-100 px-2 py-1 text-xs text-yellow-800"
                >
                  {{ t('photoRequired') }}
                </span>
              </p>
            </div>
          </label>
        </div>

        <div
          v-if="guidelinesText"
          class="space-y-2 rounded-md bg-hazel-50 p-4 text-sm text-obsidian-800"
        >
          <h5 class="font-medium">{{ t('guidelines') }}</h5>
          <div class="whitespace-pre-line">{{ guidelinesText }}</div>
        </div>

        <div v-if="selectedFeeId && selectedFee?.photoRequired" class="space-y-2">
          <h5 class="font-medium">{{ t('uploadPhoto') }}</h5>
          <UploadDropzone
            v-model:files="selectedPhotos"
            :empty-hint="t('dropzoneHint')"
            :add-more-hint="t('dropzoneAddMore')"
            :remove-label="t('remove')"
          />
          <p
            v-if="selectedFee.photoRequired && selectedPhotos.length === 0"
            class="text-xs text-red-600"
          >
            {{ t('photoRequiredForThisFee') }}
          </p>
        </div>

        <div class="flex justify-end gap-2">
          <CVButton variant="outline" @click="showModal = false">
            {{ t('cancel') }}
          </CVButton>
          <CVButton
            variant="default"
            :disabled="
              !selectedFeeId ||
              (selectedFee?.photoRequired && selectedPhotos.length === 0) ||
              isAdding
            "
            :is-loading="isAdding"
            @click="handleAddCleaningFee"
          >
            {{ t('add') }}
          </CVButton>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAvailableCleaningFees, useAddCleaningFee } from '@/queries/use-cleaning-fees';
import { useUploadCleaningFeePhoto } from '@/queries/use-upload';
import CVButton from '@/components/buttons/CVButton.vue';
import Modal from '@/components/Modal.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { Local, type CleaningFeeDto } from '@carvia/ros-client-types';
import { radiobutton } from '@carvia/theme/lib/cva/radiobutton';
import UploadDropzone from '@/components/UploadDropzone.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { getLocalizedField } from '@/utils/get-localized-field';

interface Props {
  bookingId: string;
  customerLocale: Local;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'cleaning-fee-added'): void;
}>();

const { t, locale } = useI18n();

const showModal = ref(false);
const selectedFeeId = ref<string>('');
const selectedPhotos = ref<{ name: string; url: string }[]>([]);
const isAdding = ref(false);

const { data: availableFees, isLoading } = useAvailableCleaningFees();
const fees = computed(() => availableFees.value || []);
const { mutateAsync: addCleaningFee } = useAddCleaningFee(props.bookingId);
const { mutateAsync: uploadCleaningFeePhoto } = useUploadCleaningFeePhoto();

const selectedFee = computed<CleaningFeeDto | undefined>(() => {
  return fees.value.find((fee) => fee.id === selectedFeeId.value);
});

const guidelinesText = computed(() => {
  return selectedFee.value?.guidelines
    ? getLocalizedField(selectedFee.value.guidelines, locale.value)
    : '';
});

const handleAddCleaningFee = async () => {
  if (!selectedFeeId.value) return;

  try {
    isAdding.value = true;

    const fileKeys: string[] = [];
    for (const photo of selectedPhotos.value) {
      const res = await fetch(photo.url);
      const blob = await res.blob();
      const { fileKey } = await uploadCleaningFeePhoto({
        file: blob,
        params: { bookingId: props.bookingId },
      });
      fileKeys.push(fileKey);
    }

    await addCleaningFee({
      cleaningFeeId: selectedFeeId.value,
      photoFileKeys: fileKeys.length > 0 ? fileKeys : undefined,
      locale: props.customerLocale,
    });

    showModal.value = false;
    selectedFeeId.value = '';

    emit('cleaning-fee-added');
  } finally {
    isAdding.value = false;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "addCleaningFee": "Add Cleaning Fee",
    "noCleaningFeesAvailable": "No cleaning fees available",
    "photoRequired": "Photo required",
    "uploadPhoto": "Upload Photos",
    "photo": "Photo",
    "remove": "Remove",
    "selectPhotos": "Select Photos",
    "photoRequiredForThisFee": "At least one photo is required for this cleaning fee",
    "add": "Add",
    "cancel": "Cancel",
    "guidelines": "Guidelines",
    "dropzoneHint": "Drag & drop images here or click to select",
    "dropzoneAddMore": "Drag & drop more images here or click to add more"
  },
  "de": {
    "addCleaningFee": "Reinigungsgebühr hinzufügen",
    "noCleaningFeesAvailable": "Keine Reinigungsgebühren verfügbar",
    "photoRequired": "Foto erforderlich",
    "uploadPhoto": "Fotos hochladen",
    "photo": "Foto",
    "remove": "Entfernen",
    "selectPhotos": "Fotos auswählen",
    "photoRequiredForThisFee": "Für diese Reinigungsgebühr ist mindestens ein Foto erforderlich",
    "add": "Hinzufügen",
    "cancel": "Abbrechen",
    "guidelines": "Hinweise",
    "dropzoneHint": "Bilder hierher ziehen oder klicken zum Auswählen",
    "dropzoneAddMore": "Weitere Bilder hierher ziehen oder klicken zum Hinzufügen"
  },
  "es": {
    "addCleaningFee": "Añadir tasa de limpieza",
    "noCleaningFeesAvailable": "No hay tasas de limpieza disponibles",
    "photoRequired": "Foto obligatoria",
    "uploadPhoto": "Subir fotos",
    "photo": "Foto",
    "remove": "Quitar",
    "selectPhotos": "Seleccionar fotos",
    "photoRequiredForThisFee": "Se requiere al menos una foto para esta tasa de limpieza",
    "add": "Añadir",
    "cancel": "Cancelar",
    "guidelines": "Indicaciones",
    "dropzoneHint": "Arrastre imágenes aquí o haga clic para seleccionar",
    "dropzoneAddMore": "Arrastre más imágenes aquí o haga clic para añadir"
  }
}
</i18n>
